import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { useNamespace } from '../lib/hooks'
import { api } from '../lib/api'

export function NamespaceSettings() {
  const { slug = '' } = useParams()
  const navigate      = useNavigate()
  const ns            = useNamespace(slug)

  const [form, setForm]         = useState({ name: '', is_private: true, allow_overwrite: false })
  const [busy, setBusy]         = useState(false)
  const [delBusy, setDelBusy]   = useState(false)
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null)

  // Sync form once namespace data arrives
  useEffect(() => {
    if (ns.data) {
      setForm({
        name: ns.data.name,
        is_private: Boolean(ns.data.is_private),
        allow_overwrite: Boolean(ns.data.allow_overwrite),
      })
    }
  }, [ns.data])

  const canAdmin = ['owner', 'admin'].includes(ns.data?.role ?? '')
  const isOwner  = ns.data?.role === 'owner'

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setFeedback(null)
    try {
      await api.patch(`/api/namespaces/${slug}`, form)
      setFeedback({ ok: true, msg: 'Settings saved.' })
      ns.refetch()
    } catch (err) {
      setFeedback({ ok: false, msg: (err as Error).message })
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete() {
    const confirm1 = window.confirm(
      `Delete namespace "${slug}"? All modules and versions will be permanently removed.`,
    )
    if (!confirm1) return
    const input = window.prompt(`Type "${slug}" to confirm deletion:`)
    if (input !== slug) {
      window.alert('Confirmation did not match. Namespace not deleted.')
      return
    }
    setDelBusy(true)
    try {
      await api.delete(`/api/namespaces/${slug}`)
      navigate('/namespaces')
    } catch (err) {
      setFeedback({ ok: false, msg: (err as Error).message })
      setDelBusy(false)
    }
  }

  if (ns.loading) return <Layout title="Settings"><div className="empty-state">Loading…</div></Layout>
  if (!canAdmin)  return <Layout title="Settings"><div className="empty-state" style={{ color: 'var(--red)' }}>Access denied.</div></Layout>

  return (
    <Layout title={`${slug} — Settings`}>
      {/* General settings */}
      <div className="card fu" style={{ maxWidth: 560, marginBottom: 24 }}>
        <div className="card-header"><div className="kicker">General</div></div>
        <div className="card-body">
          <form onSubmit={handleSave} style={{ display: 'grid', gap: 16 }}>
            <div>
              <label className="field-label">Display name</label>
              <input
                className="input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>

            <div className="divider" />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text)', marginBottom: 2 }}>
                    Private namespace
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>
                    Only members can download modules
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={form.is_private}
                  onChange={(e) => setForm({ ...form, is_private: e.target.checked })}
                  style={{ accentColor: 'var(--accent)', width: 16, height: 16 }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text)', marginBottom: 2 }}>
                    Allow version overwrite
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>
                    Permit republishing an existing semver (mutable registry)
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={form.allow_overwrite}
                  onChange={(e) => setForm({ ...form, allow_overwrite: e.target.checked })}
                  style={{ accentColor: 'var(--accent)', width: 16, height: 16 }}
                />
              </div>
            </div>

            {feedback && (
              <div className={feedback.ok ? 'feedback-success' : 'feedback-error'}>
                {feedback.msg}
              </div>
            )}

            <div>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? <span className="spinner" /> : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Danger zone */}
      {isOwner && (
        <div className="danger-zone fu" style={{ maxWidth: 560 }}>
          <div className="kicker" style={{ color: 'var(--red)', marginBottom: 8 }}>Danger zone</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text)', marginBottom: 2 }}>
                Delete namespace
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>
                Permanently deletes all modules, versions and members. Cannot be undone.
              </div>
            </div>
            <button
              className="btn btn-danger"
              disabled={delBusy}
              onClick={handleDelete}
            >
              {delBusy ? <span className="spinner" /> : 'Delete namespace'}
            </button>
          </div>
        </div>
      )}
    </Layout>
  )
}
